// Recovery guard for iOS Home Screen (standalone) web apps.
//
// In standalone mode there is no browser tab and no silent-reload UI. When iOS
// reclaims memory or suspends a backgrounded WebKit process for too long, the
// app can come back with a dead JS context: no scrolling, no taps, only a hard
// quit recovers it. Safari tabs hide this by transparently reloading; standalone
// apps don't, so we reload ourselves on the two signals that correlate with a
// discarded/suspended context.
//
// Opt out while a view holds unsaved state, so a recovery reload can't drop
// in-progress edits. Two ways:
//
//   - In React, call the `useBlockAutoReload(active)` hook below (preferred —
//     it reference-counts, so multiple dirty editors compose correctly).
//   - Outside React, set `window.__tuberyBlockAutoReload = true` and clear it
//     when it's safe to reload again.

import * as React from "react";

declare global {
  interface Window {
    __tuberyBlockAutoReload?: boolean;
  }
}

// If the app was hidden longer than this, treat resume as a likely
// discarded/suspended context and reload to get a clean tree.
const STALE_AFTER_MS = 30 * 60 * 1000; // 30 minutes

// Reference count of active "hold the reload" requests. A plain boolean breaks
// when two surfaces are dirty at once and one clears: it would unblock while the
// other still has unsaved work. Counting keeps the block up until the last
// holder releases.
let blockCount = 0;

// Register an unsaved-work hold. Returns a release fn (idempotent). Pair every
// call with its release — typically via the React hook below or an effect
// cleanup — or the app will never auto-recover.
export function blockAutoReload(): () => void {
  blockCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    blockCount -= 1;
  };
}

// React hook: hold the reload open while `active` is true (e.g. a dirty editor
// with an unsaved/in-flight autosave). Releases automatically on cleanup, so an
// unmount mid-edit can't leave the app permanently un-recoverable.
export function useBlockAutoReload(active: boolean): void {
  React.useEffect(() => {
    if (!active) return;
    return blockAutoReload();
  }, [active]);
}

function autoReloadBlocked(): boolean {
  return window.__tuberyBlockAutoReload === true || blockCount > 0;
}

export function installStandaloneRecovery(): void {
  // Only relevant when launched from the Home Screen. matchMedia covers
  // modern iOS; navigator.standalone is the legacy iOS Safari signal.
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (!standalone) return;

  // 1) Back-forward cache restore. When a page is restored from bfcache after
  //    the process was suspended, persisted === true. The JS context is often
  //    stale/frozen in standalone mode, so force a fresh load.
  window.addEventListener("pageshow", (event) => {
    if ((event as PageTransitionEvent).persisted && !autoReloadBlocked()) {
      window.location.reload();
    }
  });

  // 2) Long background suspension. Record when we go hidden; on return, if we
  //    were gone long enough to have likely been discarded, reload.
  let hiddenAt = 0;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      return;
    }
    if (document.visibilityState === "visible" && hiddenAt > 0) {
      const awayMs = Date.now() - hiddenAt;
      hiddenAt = 0;
      if (awayMs > STALE_AFTER_MS && !autoReloadBlocked()) {
        window.location.reload();
      }
    }
  });
}
